import React, { useEffect, useState } from 'react';
import {
  LayoutRequest, LayoutResponse, SiteBBox,
  TrackerEquipmentRead, ModuleEquipmentRead,
} from '../types';
import { layoutApi, projectApi, billingApi, equipmentApi } from '../api/client';

interface Props {
  siteBox:        SiteBBox | null;
  sitePolygon?:   number[][] | null;
  onResult:       (r: LayoutResponse, req: LayoutRequest) => void;
  onProjectSaved: () => void;
  userEmail:      string;
  userPlan?:      string;
  onLogout:       () => void;
}

function bboxToMetres(box: SiteBBox): { w: number; h: number; lat: number } {
  const midLat = (box[1] + box[3]) / 2;
  const w = Math.abs(box[2] - box[0]) * 111_320 * Math.cos(midLat * Math.PI / 180);
  const h = Math.abs(box[3] - box[1]) * 110_540;
  return { w: Math.round(w), h: Math.round(h), lat: parseFloat(midLat.toFixed(4)) };
}

const ALBEDO_PRESETS: { label: string; value: number }[] = [
  { label: 'Grass / crops',  value: 0.20 },
  { label: 'Gravel / dirt',  value: 0.25 },
  { label: 'Light sand',     value: 0.35 },
  { label: 'Concrete',       value: 0.30 },
  { label: 'Snow',           value: 0.65 },
  { label: 'Custom',         value: -1   },
];

export function ParameterPanel({
  siteBox, sitePolygon, onResult, onProjectSaved, userEmail, userPlan, onLogout,
}: Props) {
  // ── Equipment library ──────────────────────────────────────────────────────
  const [trackers,   setTrackers]   = useState<TrackerEquipmentRead[]>([]);
  const [modules,    setModules]    = useState<ModuleEquipmentRead[]>([]);
  const [trackerId,  setTrackerId]  = useState<string>('');
  const [moduleId,   setModuleId]   = useState<string>('');
  const [eqLoading,  setEqLoading]  = useState(true);

  // ── Layout params ──────────────────────────────────────────────────────────
  const [gcr,      setGcr]      = useState(0.40);
  const [dcAc,     setDcAc]     = useState(1.30);
  const [ghi,      setGhi]      = useState(1900);
  const [coupling, setCoupling] = useState<'AC' | 'DC'>('AC');
  const [optimise, setOptimise] = useState(true);

  // ── Boundary setback ─────────────────────────────────────────────────────
  const [setback, setSetback] = useState(10);

  // ── Target DC capacity ─────────────────────────────────────────────────────
  const [targetDcMode,  setTargetDcMode]  = useState(false);
  const [targetDcMwp,   setTargetDcMwp]   = useState(50);

  // ── Spine road spacing ───────────────────────────────────────────────────
  const [spineSpacing,  setSpineSpacing]  = useState(500);
  const [spineEnabled,  setSpineEnabled]  = useState(false);

  // ── BESS MW × hours ───────────────────────────────────────────────────────
  const [bessMw,        setBessMw]        = useState(25);
  const [bessHours,     setBessHours]     = useState(4);

  // ── Inverter topology & connection voltage ────────────────────────────────
  const [inverterType,  setInverterType]  = useState<'central' | 'string_mvpd'>('central');
  const [invModel,      setInvModel]      = useState('sungrow_sg3125hv');
  const [gridVoltageKv, setGridVoltageKv] = useState(33);

  // ── Bifacial & albedo ──────────────────────────────────────────────────────
  const [bifacialEnabled, setBifacialEnabled] = useState(false);
  const [bifFactor,       setBifFactor]       = useState(0.70);
  const [albedoPreset,    setAlbedoPreset]    = useState('Gravel / dirt');
  const [albedoCustom,    setAlbedoCustom]    = useState(0.25);

  const resolvedAlbedo = (() => {
    const preset = ALBEDO_PRESETS.find(p => p.label === albedoPreset);
    return preset && preset.value >= 0 ? preset.value : albedoCustom;
  })();

  // ── UI state ───────────────────────────────────────────────────────────────
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');
  const [result,       setResult]       = useState<LayoutResponse | null>(null);
  const [lastReq,      setLastReq]      = useState<LayoutRequest | null>(null);
  const [projectName,  setProjectName]  = useState('');
  const [showSave,     setShowSave]     = useState(false);

  // Derive site bbox from polygon vertices when available.
  // siteBox is set from polygon bbox (bboxFromCoords) by MapView — they should match.
  // We also derive activeBbox inline from polygon vertices for origin accuracy.
  const polyBbox: SiteBBox | null = (() => {
    if (!sitePolygon || sitePolygon.length < 3) return null;
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    for (const pt of sitePolygon) {
      if (pt[0] < minLng) minLng = pt[0]; if (pt[1] < minLat) minLat = pt[1];
      if (pt[0] > maxLng) maxLng = pt[0]; if (pt[1] > maxLat) maxLat = pt[1];
    }
    if (!isFinite(minLng)) return null;
    return [minLng, minLat, maxLng, maxLat];
  })();
  const activeBbox = polyBbox ?? siteBox;
  const dims       = activeBbox ? bboxToMetres(activeBbox) : null;

  // ── Fetch equipment library ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setEqLoading(true);
    Promise.all([equipmentApi.trackers(), equipmentApi.modules()])
      .then(([t, m]) => {
        if (cancelled) return;
        setTrackers(t);
        setModules(m);
        const firstSat = t.find(x => x.tracker_type === 'SAT');
        if (firstSat) { setTrackerId(firstSat.id); setGcr(firstSat.gcr_typical); }
        if (m.length > 0) {
          setModuleId(m[0].id);
          if (m[0].bifacial) {
            setBifacialEnabled(true);
            setBifFactor(m[0].bifaciality_factor);
          }
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setEqLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleTrackerChange = (id: string) => {
    setTrackerId(id);
    const t = trackers.find(x => x.id === id);
    if (t) setGcr(parseFloat(t.gcr_typical.toFixed(2)));
  };

  const handleModuleChange = (id: string) => {
    setModuleId(id);
    const m = modules.find(x => x.id === id);
    if (m) {
      if (m.bifacial) {
        setBifacialEnabled(true);
        setBifFactor(parseFloat(m.bifaciality_factor.toFixed(2)));
      } else {
        setBifacialEnabled(false);
      }
    }
  };

  const selectedTracker = trackers.find(x => x.id === trackerId) ?? null;
  const selectedModule  = modules.find(x => x.id === moduleId)   ?? null;

  // ── Derived pitch ──────────────────────────────────────────────────────────
  // table_w = panels_wide × panel_w_m;  pitch = table_w / gcr
  const panelWm    = selectedModule ? selectedModule.width_mm / 1000 : 1.134;
  const panelsWide = selectedTracker ? selectedTracker.panels_wide   : 1;
  const tableW     = panelWm * panelsWide;
  const derivedPitch = gcr > 0 ? tableW / gcr : 0;

  // ── Build request ──────────────────────────────────────────────────────────
  const buildRequest = (): LayoutRequest => {
    const base: LayoutRequest = {
      tracker: {
        gcr,
        bif_factor:         bifacialEnabled ? bifFactor : 0.0,
        albedo:             bifacialEnabled ? resolvedAlbedo : 0.25,
        // Physical tracker dimensions — from equipment library or UI defaults
        panels_wide:        selectedTracker?.panels_wide        ?? 1,
        panels_per_segment: selectedTracker?.panels_per_segment ?? 30,
        tracker_segments:   selectedTracker?.tracker_segments   ?? 1,
        gap_between_segs:   selectedTracker?.gap_between_segs_m ?? 0.1,
        module_x_gap_m:     0.02,
        module_y_gap_m:     0.02,
      },
      inverter:     { dc_ac_ratio: dcAc, inverter_type: inverterType, central_inv_model: invModel },
      bess:         {
        bess_coupling: coupling,
        ...(bessMw > 0 && bessHours > 0
          ? { target_bess_mw: bessMw, target_bess_hours: bessHours }
          : { bess_mwh: bessMw * Math.max(bessHours, 1) }),
      },
      yield_p:      { ghi_kwh_m2_yr: ghi },
      optimise_gcr: optimise,
    };
    if (dims) {
      base.site = {
        site_w:           dims.w,
        site_h:           dims.h,
        latitude:         dims.lat,
        grid_voltage_kv:  gridVoltageKv,
        setback_all:      setback,
        // SW corner geo-reference — required for site polygon masking
        origin_lng:       activeBbox![0],
        origin_lat:       activeBbox![1],
        ...(targetDcMode && targetDcMwp > 0 ? { target_dc_mwp: targetDcMwp } : {}),
        spine_road_spacing_m: spineEnabled ? spineSpacing : 0,
        ...(sitePolygon && sitePolygon.length >= 3
          ? { site_polygon: sitePolygon }
          : {}),
      };
    }
    if (trackerId || moduleId) {
      base.equipment = {
        tracker_id:           trackerId  || null,
        module_id:            moduleId   || null,
        tracker_display_name: selectedTracker
          ? `${selectedTracker.manufacturer} ${selectedTracker.model}` : null,
        module_display_name:  selectedModule
          ? `${selectedModule.manufacturer} ${selectedModule.model}` : null,
      };
    }
    return base;
  };

  const handleGenerate = async () => {
    setError('');
    setLoading(true);
    const req = buildRequest();
    try {
      const res = await layoutApi.generate(req);
      setResult(res);
      setLastReq(req);
      onResult(res, req);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      // Surface the actual API error so users can diagnose issues
      const axiosErr  = err as any;
      const apiDetail = axiosErr?.response?.data?.detail;
      const status    = axiosErr?.response?.status as number | undefined;
      if (status === 422 && apiDetail) {
        const msg = Array.isArray(apiDetail)
          ? apiDetail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(' | ')
          : String(apiDetail);
        setError(`Validation error: ${msg}`);
      } else if (status === 500) {
        setError('Server error — check Railway logs. The layout engine may have crashed.');
      } else if (axiosErr?.code === 'ECONNABORTED' || axiosErr?.message?.includes('timeout')) {
        setError('Request timed out. Try a smaller site or reduce target DC capacity.');
      } else {
        setError(apiDetail ? String(apiDetail) : `Layout failed (${status ?? 'network error'}). Check site boundary and parameters.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!lastReq || !result) return;
    setSaving(true);
    try {
      await projectApi.create(lastReq, projectName || 'Unnamed project');
      setShowSave(false);
      setProjectName('');
      onProjectSaved();
    } catch {
      setError('Failed to save project.');
    } finally {
      setSaving(false);
    }
  };

  const satTrackers   = trackers.filter(t => t.tracker_type === 'SAT');
  const fixedTrackers = trackers.filter(t => t.tracker_type === 'FIXED');
  const modulesByMfr  = modules.reduce<Record<string, ModuleEquipmentRead[]>>((acc, m) => {
    (acc[m.manufacturer] = acc[m.manufacturer] || []).push(m);
    return acc;
  }, {});

  const S = styles;

  return (
    <div style={S.panel}>

      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.logo}>⚡ Solar Farm</div>
          <div style={S.userBadge}>{userEmail}</div>
        </div>
        <button style={S.logoutBtn} onClick={onLogout}>Log out</button>
      </div>

      {userPlan !== 'pro' && userPlan !== 'enterprise' && (
        <button
          style={S.upgradeBtn}
          onClick={async () => {
            try {
              const { url } = await billingApi.checkout('price_1TJitPRpMtyBoQPAB8xuetSX');
              window.location.href = url;
            } catch {
              setError('Could not start checkout. Please try again.');
            }
          }}
        >
          ⭐ Upgrade to Pro — $49 AUD/month
        </button>
      )}

      {/* Site */}
      <div style={S.section}>
        <div style={S.sectionTitle}>Site</div>
        {dims ? (
          <div style={S.dimBox}>
            <span style={S.dimLabel}>Width (E–W)</span>
            <span style={S.dimVal}>{dims.w.toLocaleString()} m</span>
            <span style={S.dimLabel}>Height (N–S)</span>
            <span style={S.dimVal}>{dims.h.toLocaleString()} m</span>
            <span style={S.dimLabel}>Latitude</span>
            <span style={S.dimVal}>{dims.lat}°</span>
            <span style={S.dimLabel}>Area (gross)</span>
            <span style={S.dimVal}>{((dims.w * dims.h) / 10_000).toFixed(0)} ha</span>
          </div>
        ) : (
          <div style={S.hint}>Click on the map to draw your site boundary</div>
        )}
      </div>

      {/* Equipment */}
      <div style={S.section}>
        <div style={S.sectionTitle}>Equipment</div>
        {eqLoading ? (
          <div style={S.eqLoading}>Loading equipment library…</div>
        ) : (
          <>
            <div style={S.eqLabel}>Tracker</div>
            <select style={S.eqSelect} value={trackerId}
              onChange={e => handleTrackerChange(e.target.value)}>
              <option value="">— Manual params —</option>
              {satTrackers.length > 0 && (
                <optgroup label="Single-Axis Tracker (SAT)">
                  {satTrackers.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.manufacturer} {t.model}
                    </option>
                  ))}
                </optgroup>
              )}
              {fixedTrackers.length > 0 && (
                <optgroup label="Fixed Tilt">
                  {fixedTrackers.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.manufacturer} {t.model}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {selectedTracker && (
              <div style={S.specChip}>
                <span>{selectedTracker.tracker_type}</span>
                <span>·</span>
                <span>{selectedTracker.panels_per_segment * selectedTracker.tracker_segments} mod/row</span>
                <span>·</span>
                <span>{selectedTracker.piles_per_row} piles</span>
                <span>·</span>
                <span>max slope {selectedTracker.max_slope_ns_pct}%</span>
              </div>
            )}

            <div style={{ ...S.eqLabel, marginTop: 10 }}>PV Module</div>
            <select style={S.eqSelect} value={moduleId}
              onChange={e => handleModuleChange(e.target.value)}>
              <option value="">— Manual params —</option>
              {Object.entries(modulesByMfr).map(([mfr, mods]) => (
                <optgroup key={mfr} label={mfr}>
                  {mods.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.model} — {m.power_wp}Wp
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selectedModule && (
              <div style={S.specChip}>
                <span>{selectedModule.power_wp}Wp</span>
                <span>·</span>
                <span>Voc {selectedModule.voc}V</span>
                <span>·</span>
                <span>{selectedModule.cell_type}</span>
                {selectedModule.bifacial && (
                  <>
                    <span>·</span>
                    <span style={{ color: '#17607A', fontWeight: 600 }}>
                      bifacial {(selectedModule.bifaciality_factor * 100).toFixed(0)}%
                    </span>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Layout */}
      <div style={S.section}>
        <div style={S.sectionTitle}>Layout</div>

        {/* Boundary setback */}
        <Slider label="Boundary setback" value={setback} min={0} max={50} step={1}
          display={`${setback} m`} onChange={setSetback} />
        <div style={S.fieldHint}>
          Min 10 m recommended (AS 61730 / WA council conditions). Applied to all site boundaries.
        </div>

        {/* GCR slider + derived pitch chip */}
        <div style={sliderStyles.wrap}>
          <div style={sliderStyles.row}>
            <span style={sliderStyles.label}>Starting GCR</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={sliderStyles.val}>{gcr.toFixed(2)}</span>
              {derivedPitch > 0 && (
                <span style={S.pitchChip}>
                  pitch {derivedPitch.toFixed(2)} m
                </span>
              )}
            </div>
          </div>
          <input type="range" min={0.20} max={0.55} step={0.01} value={gcr}
            onChange={e => setGcr(parseFloat(e.target.value))}
            style={sliderStyles.input} />
        </div>

        <Slider label="DC:AC Ratio" value={dcAc} min={1.0} max={1.6} step={0.05}
          display={dcAc.toFixed(2)} onChange={setDcAc} />
        {/* DC:AC warning if aggressive */}
        {dcAc > 1.45 && (
          <div style={S.warningChip}>⚠ {dcAc.toFixed(2)} — aggressive clipping territory</div>
        )}
        <Toggle label="Optimise GCR for max DC" value={optimise} onChange={setOptimise} />

        {/* Target DC toggle */}
        <div style={{ marginTop: 8 }}>
          <Toggle label="Set target DC capacity" value={targetDcMode} onChange={setTargetDcMode} />
        </div>
        {targetDcMode && (
          <div style={{ marginTop: 6 }}>
            <div style={sliderStyles.wrap}>
              <div style={sliderStyles.row}>
                <span style={sliderStyles.label}>Target DC</span>
                <span style={sliderStyles.val}>{targetDcMwp} MWp</span>
              </div>
              <input type="range" min={1} max={500} step={1} value={targetDcMwp}
                onChange={e => setTargetDcMwp(parseFloat(e.target.value))}
                style={sliderStyles.input} />
              <div style={S.fieldHint}>
                Layout fills S→N until target is reached. Remaining rows left empty.
              </div>
            </div>
          </div>
        )}


        {/* N–S Spine roads */}
        <div style={{ marginTop: 8 }}>
          <Toggle label="N–S spine roads" value={spineEnabled} onChange={setSpineEnabled} />
        </div>
        {spineEnabled && (
          <div style={{ marginTop: 6 }}>
            <div style={S.eqLabel}>Bay width (m between spine roads)</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const, marginBottom: 6 }}>
              {[250, 500, 750, 1000].map(w => (
                <button key={w}
                  onClick={() => setSpineSpacing(w)}
                  style={{
                    padding: '4px 10px', fontSize: 12, borderRadius: 12, cursor: 'pointer',
                    border: `1.5px solid ${spineSpacing === w ? C.teal : C.border}`,
                    background: spineSpacing === w ? C.teal : '#f7f9fc',
                    color: spineSpacing === w ? '#fff' : C.text,
                    fontWeight: spineSpacing === w ? 700 : 400,
                  }}>
                  {w}m
                </button>
              ))}
            </div>
            <div style={S.fieldHint}>
              One inverter station placed per bay. 500m = ~4–6 rows per bay at typical pitch.
            </div>
          </div>
        )}
      </div>

      {/* Bifacial & Albedo */}
      <div style={S.section}>
        <div style={S.sectionTitle}>Bifacial &amp; Albedo</div>

        <Toggle label="Enable bifacial rear-side gain" value={bifacialEnabled}
          onChange={setBifacialEnabled} />

        {bifacialEnabled && (
          <div style={S.bifacialBox}>
            {/* Bifaciality factor */}
            <div style={sliderStyles.wrap}>
              <div style={sliderStyles.row}>
                <span style={sliderStyles.label}>Bifaciality factor</span>
                <span style={sliderStyles.val}>{(bifFactor * 100).toFixed(0)}%</span>
              </div>
              <input type="range" min={0.50} max={0.85} step={0.01} value={bifFactor}
                onChange={e => setBifFactor(parseFloat(e.target.value))}
                style={sliderStyles.input} />
              <div style={S.fieldHint}>
                Fraction of front-side efficiency from rear.
                Mono PERC ≈ 70%, TOPCon ≈ 75–85%.
              </div>
            </div>

            {/* Albedo preset */}
            <div style={S.eqLabel}>Ground albedo</div>
            <select
              style={S.eqSelect}
              value={albedoPreset}
              onChange={e => setAlbedoPreset(e.target.value)}
            >
              {ALBEDO_PRESETS.map(p => (
                <option key={p.label} value={p.label}>
                  {p.label}{p.value >= 0 ? ` — ${(p.value * 100).toFixed(0)}%` : ''}
                </option>
              ))}
            </select>

            {albedoPreset === 'Custom' && (
              <div style={{ ...sliderStyles.wrap, marginTop: 8 }}>
                <div style={sliderStyles.row}>
                  <span style={sliderStyles.label}>Albedo</span>
                  <span style={sliderStyles.val}>{(albedoCustom * 100).toFixed(0)}%</span>
                </div>
                <input type="range" min={0.05} max={0.70} step={0.01}
                  value={albedoCustom}
                  onChange={e => setAlbedoCustom(parseFloat(e.target.value))}
                  style={sliderStyles.input} />
              </div>
            )}

            {/* Live bifacial gain estimate */}
            <div style={S.bifacialGainChip}>
              Est. rear-side gain ≈{' '}
              <strong>
                {(bifFactor * resolvedAlbedo * (1 - 0.8 * gcr) * 100).toFixed(1)}%
              </strong>
              {' '}on DC output
            </div>
          </div>
        )}
      </div>

      {/* BESS */}
      <div style={S.section}>
        <div style={S.sectionTitle}>BESS</div>

        {/* MW power rating */}
        <Slider label="Power" value={bessMw} min={0} max={500} step={5}
          display={`${bessMw} MW`} onChange={setBessMw} />

        {/* Duration hours — pill buttons */}
        <div style={S.eqLabel}>Duration</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const, marginBottom: 8 }}>
          {[0, 2, 4, 6, 8, 10, 12, 14, 16].map(h => (
            <button key={h}
              onClick={() => setBessHours(h)}
              style={{
                padding: '4px 10px', fontSize: 12, borderRadius: 12, cursor: 'pointer',
                border: `1.5px solid ${bessHours === h ? C.teal : C.border}`,
                background: bessHours === h ? C.teal : '#f7f9fc',
                color: bessHours === h ? '#fff' : C.text,
                fontWeight: bessHours === h ? 700 : 400,
              }}>
              {h === 0 ? 'None' : `${h}h`}
            </button>
          ))}
        </div>

        {/* Derived MWh chip */}
        {bessMw > 0 && bessHours > 0 && (
          <div style={{ ...S.bifacialGainChip, marginBottom: 8 }}>
            <strong>{bessMw} MW × {bessHours}h = {bessMw * bessHours} MWh</strong>
          </div>
        )}

        {/* Placement hint */}
        {bessMw > 0 && bessHours > 0 && (
          <div style={S.fieldHint}>
            {bessMw * bessHours <= 20 && coupling === 'DC'
              ? '⚡ DC in-field placement (≤ 20 MWh)'
              : coupling === 'DC'
              ? '🏗 DC south-compound placement (> 20 MWh)'
              : '🏗 AC south-compound placement'}
          </div>
        )}

        {/* AC / DC coupling */}
        <div style={S.couplingRow}>
          {(['AC', 'DC'] as const).map((c) => (
            <button key={c} onClick={() => setCoupling(c)}
              style={{ ...S.couplingBtn, ...(coupling === c ? S.couplingActive : {}) }}>
              {c}-Coupled
            </button>
          ))}
        </div>
      </div>


      {/* Inverter & Grid Connection */}
      <div style={S.section}>
        <div style={S.sectionTitle}>Inverter &amp; Grid</div>

        {/* Inverter topology */}
        <div style={S.eqLabel}>Inverter type</div>
        <div style={S.couplingRow}>
          {(['central', 'string_mvpd'] as const).map(t => (
            <button key={t} onClick={() => {
              setInverterType(t);
              setInvModel(t === 'central' ? 'sungrow_sg3125hv' : 'sungrow_350hx_skid');
            }}
              style={{ ...S.couplingBtn, ...(inverterType === t ? S.couplingActive : {}) }}>
              {t === 'central' ? 'Central' : 'String / MVPD'}
            </button>
          ))}
        </div>

        {/* Inverter model */}
        <div style={{ ...S.eqLabel, marginTop: 8 }}>Model</div>
        <select style={S.eqSelect} value={invModel} onChange={e => setInvModel(e.target.value)}>
          {inverterType === 'central' ? (
            <>
              <option value="sungrow_sg3125hv">Sungrow SG3125HV-20 — 3,125 kW</option>
              <option value="sungrow_sg2500u">Sungrow SG2500U — 2,500 kW</option>
              <option value="sma_sc3000ev">SMA Sunny Central 3000-EV — 3,000 kW</option>
              <option value="sma_sc2500ev">SMA Sunny Central 2500-EV — 2,500 kW</option>
              <option value="huawei_sun2000_3312ktl">Huawei SUN2000-3312KTL-M1 — 3,312 kW</option>
              <option value="abb_pvs980_2000">ABB PVS980-58 2000kVA — 2,000 kW</option>
            </>
          ) : (
            <>
              <option value="sungrow_350hx_skid">Sungrow 4×SG350HX MVPD Skid — 1,400 kW</option>
              <option value="huawei_215ktl_skid">Huawei 6×SUN2000-215KTL Skid — 1,290 kW</option>
            </>
          )}
        </select>

        {/* Grid connection voltage */}
        <div style={{ ...S.eqLabel, marginTop: 10 }}>Grid connection voltage</div>
        <select style={S.eqSelect} value={gridVoltageKv}
          onChange={e => setGridVoltageKv(Number(e.target.value))}>
          <option value={11}>11 kV — Sub-transmission</option>
          <option value={33}>33 kV — Distribution</option>
          <option value={66}>66 kV — Sub-transmission</option>
          <option value={132}>132 kV — Transmission</option>
          <option value={220}>220 kV — Transmission</option>
          <option value={330}>330 kV — High voltage transmission</option>
        </select>
        <div style={S.fieldHint}>
          WA typical: 33 kV (&lt;30 MW) · 132 kV (30–200 MW) · 330 kV (&gt;200 MW)
        </div>
      </div>

      {/* Yield */}
      <div style={S.section}>
        <div style={S.sectionTitle}>Yield</div>
        <Slider label="GHI (kWh/m²/yr)" value={ghi} min={1400} max={2200} step={50}
          display={`${ghi}`} onChange={setGhi} />
        <div style={S.fieldHint}>
          Perth ≈ 2000 · Adelaide ≈ 1900 · Sydney ≈ 1700
        </div>
      </div>

      {error && <div style={S.error}>{error}</div>}

      <button style={S.generateBtn} onClick={handleGenerate} disabled={loading || !dims}>
        {loading ? 'Running…' : '⚡ Generate Layout'}
      </button>

      {result && !showSave && (
        <button style={S.saveBtn} onClick={() => setShowSave(true)}>
          💾 Save as project
        </button>
      )}

      {showSave && (
        <div style={S.saveBox}>
          <input
            style={S.saveInput}
            type="text"
            placeholder="Project name"
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            autoFocus
          />
          <div style={S.saveActions}>
            <button style={S.cancelBtn} onClick={() => setShowSave(false)}>Cancel</button>
            <button style={S.confirmSaveBtn} onClick={handleSave} disabled={saving}>
              {saving ? '…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Slider({ label, value, min, max, step, display, onChange }: {
  label: string; value: number; min: number; max: number; step: number;
  display: string; onChange: (v: number) => void;
}) {
  return (
    <div style={sliderStyles.wrap}>
      <div style={sliderStyles.row}>
        <span style={sliderStyles.label}>{label}</span>
        <span style={sliderStyles.val}>{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={sliderStyles.input} />
    </div>
  );
}

function Toggle({ label, value, onChange }: {
  label: string; value: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label style={toggleStyles.wrap}>
      <input type="checkbox" checked={value}
        onChange={e => onChange(e.target.checked)}
        style={toggleStyles.check} />
      <span style={toggleStyles.label}>{label}</span>
    </label>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const C = { navy: '#1F3864', blue: '#2E5FA3', teal: '#17607A', text: '#404040', border: '#e0e5ee' };

const styles: Record<string, React.CSSProperties> = {
  panel: {
    display: 'flex', flexDirection: 'column', height: '100%',
    background: '#fff', overflowY: 'auto',
    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif", fontSize: 14,
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 16px 14px', background: C.navy, color: '#fff',
    borderBottom: `2px solid ${C.blue}`,
  },
  logo:      { fontWeight: 700, fontSize: 16, letterSpacing: '-0.3px' },
  userBadge: { fontSize: 11, opacity: 0.65, marginTop: 2 },
  logoutBtn: {
    background: 'rgba(255,255,255,0.1)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 5, padding: '5px 10px', fontSize: 12, cursor: 'pointer',
  },
  upgradeBtn: {
    margin: '8px 16px', padding: '10px 0',
    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
    color: '#fff', border: 'none', borderRadius: 8,
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  section:      { padding: '14px 16px 10px', borderBottom: `1px solid ${C.border}` },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.8px', color: C.teal, marginBottom: 10,
  },
  dimBox: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: '5px 0', background: '#f7f9fc', borderRadius: 7, padding: '10px 12px',
  },
  dimLabel: { fontSize: 12, color: '#888' },
  dimVal:   { fontSize: 12, fontWeight: 600, color: C.text, textAlign: 'right' as const },
  hint: {
    fontSize: 12, color: '#999', fontStyle: 'italic',
    background: '#f7f9fc', borderRadius: 7, padding: '10px 12px',
  },
  fieldHint: { fontSize: 11, color: '#888', marginTop: 4, lineHeight: 1.4 },
  eqLoading: { fontSize: 12, color: '#999', fontStyle: 'italic', padding: '4px 0' },
  eqLabel:   { fontSize: 12, color: '#555', marginBottom: 4 },
  eqSelect: {
    width: '100%', padding: '7px 8px', fontSize: 12,
    border: `1.5px solid ${C.border}`, borderRadius: 6,
    background: '#fff', color: C.text, cursor: 'pointer',
    outline: 'none', appearance: 'auto' as any,
  },
  specChip: {
    display: 'flex', flexWrap: 'wrap' as const, gap: 4,
    marginTop: 5, padding: '5px 8px',
    background: '#f0f4fa', borderRadius: 5,
    fontSize: 11, color: '#555',
  },
  pitchChip: {
    fontSize: 11, background: '#e8f4f8', color: C.teal,
    padding: '2px 7px', borderRadius: 10, fontWeight: 500,
  },
  warningChip: {
    fontSize: 11, background: '#fff3cd', color: '#856404',
    padding: '5px 8px', borderRadius: 5, marginBottom: 8,
  },
  bifacialBox: {
    marginTop: 10, padding: '10px 12px',
    background: '#f0f9f7', borderRadius: 8,
    border: '1px solid #c0e4dc',
  },
  bifacialGainChip: {
    marginTop: 8, padding: '6px 10px',
    background: '#d5f0ea', borderRadius: 6,
    fontSize: 12, color: '#1a5e50',
  },
  couplingRow:   { display: 'flex', gap: 8, marginTop: 8 },
  couplingBtn: {
    flex: 1, padding: '8px 0', border: `1.5px solid ${C.border}`,
    borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
    background: '#f7f9fc', color: C.text,
  },
  couplingActive: { background: C.blue, color: '#fff', borderColor: C.blue },
  error: {
    margin: '0 16px', background: '#fde8e8', border: '1px solid #f8b4b4',
    borderRadius: 7, padding: '10px 12px', fontSize: 13, color: '#8b1a1a',
  },
  generateBtn: {
    margin: '14px 16px 8px', padding: '13px 0',
    background: `linear-gradient(135deg, ${C.navy}, ${C.blue})`,
    color: '#fff', border: 'none', borderRadius: 8,
    fontSize: 15, fontWeight: 700, cursor: 'pointer', letterSpacing: '-0.2px',
  },
  saveBtn: {
    margin: '0 16px 14px', padding: '10px 0',
    background: '#fff', color: C.teal, border: `1.5px solid ${C.teal}`,
    borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  saveBox: {
    margin: '0 16px 14px', padding: '12px',
    background: '#f7f9fc', borderRadius: 8, border: `1px solid ${C.border}`,
  },
  saveInput: {
    width: '100%', boxSizing: 'border-box' as const,
    border: `1.5px solid ${C.border}`, borderRadius: 6,
    padding: '9px 10px', fontSize: 13, fontFamily: 'inherit',
    outline: 'none', marginBottom: 8,
  },
  saveActions:    { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: {
    padding: '7px 14px', background: '#fff', color: '#666',
    border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, cursor: 'pointer',
  },
  confirmSaveBtn: {
    padding: '7px 18px', background: C.teal, color: '#fff',
    border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
};

const sliderStyles: Record<string, React.CSSProperties> = {
  wrap:  { marginBottom: 10 },
  row:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  label: { fontSize: 13, color: '#555' },
  val:   { fontSize: 13, fontWeight: 600, color: C.text },
  input: { width: '100%', accentColor: C.blue },
};

const toggleStyles: Record<string, React.CSSProperties> = {
  wrap:  { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4, marginBottom: 4 },
  check: { accentColor: C.blue, width: 15, height: 15, cursor: 'pointer' },
  label: { fontSize: 13, color: '#555' },
};
